import { Node, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    pageBreak: {
      /** Manuellen Word-Seitenumbruch an der Cursorposition einfügen. */
      setPageBreak: () => ReturnType;
    };
  }
}

/** HTML-Marker, den Editor, Overlay und Word-Export teilen. */
export const PAGE_BREAK_ATTR = 'data-page-break';
export const PAGE_BREAK_BEFORE_ATTR = 'data-page-break-before';
export const PAGE_BREAK_CLASS = 'stark-page-break';

export function isPageBreakElement(el: Element): boolean {
  if (el.hasAttribute(PAGE_BREAK_ATTR)) return true;
  if (el.getAttribute('data-type') === 'pageBreak') return true;
  if (el.classList.contains(PAGE_BREAK_CLASS) || el.classList.contains('node-pageBreak')) return true;
  return false;
}

export function hasPageBreakBefore(el: Element): boolean {
  if (el.getAttribute(PAGE_BREAK_BEFORE_ATTR) === 'true') return true;
  return /page-break-before\s*:\s*always/i.test(el.getAttribute('style') || '');
}

/**
 * Atomarer Block: im HTML ein leeres Div, im Word-Export `w:br type="page"`
 * (als `pageBreakBefore` des folgenden Blocks).
 */
export const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  parseHTML() {
    return [
      { tag: `div[${PAGE_BREAK_ATTR}]` },
      { tag: `hr[${PAGE_BREAK_ATTR}]` },
      { tag: `div.${PAGE_BREAK_CLASS}` },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        [PAGE_BREAK_ATTR]: 'true',
        class: PAGE_BREAK_CLASS,
        'data-testid': 'editor-page-break',
      }),
    ];
  },

  addCommands() {
    return {
      setPageBreak:
        () =>
        ({ chain, state }) => {
          const end = state.doc.content.size;
          const atEnd = state.selection.to >= end - 1;
          const next = chain().focus().insertContent({ type: this.name });
          if (atEnd) next.insertContent({ type: 'paragraph' });
          return next.run();
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Enter': () => this.editor.commands.setPageBreak(),
    };
  },
});
